<template>
  <div class="home-layout">
    <header class="home-header">
      <div class="header-inner">
        <div class="header-left">
          <router-link to="/" class="logo-link">
            <img class="logo" src="@/assets/img/layout/logo.png" alt="Gwotech Logo" />
          </router-link>
        </div>

        <nav class="header-nav header-nav--desktop" aria-label="Products">
          <div class="header-nav-links">
            <a class="header-product-link" href="/#gooval-product">{{ t('homeLayout.header.gooval') }}</a>
            <span class="header-nav-divider" aria-hidden="true"></span>
            <a class="header-product-link" href="/#insurance-product">{{ t('homeLayout.header.insurance') }}</a>
            <span class="header-nav-divider" aria-hidden="true"></span>
            <a class="header-product-link" href="/#on-chain-data">{{ t('homeLayout.header.blockchain') }}</a>
          </div>
        </nav>

        <div class="header-menu header-menu--mobile">
          <button
            type="button"
            class="header-menu-toggle"
            :aria-expanded="menuOpen ? 'true' : 'false'"
            :aria-label="menuOpen ? t('homeLayout.header.closeMenu') : t('homeLayout.header.openMenu')"
            @click="toggleMenu"
          >
            <span class="menu-toggle-icon" :class="{ open: menuOpen }">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          <Transition name="menu-collapse">
            <div v-if="menuOpen" class="header-menu-panel">
              <a class="header-menu-link" href="/#gooval-product" @click="closeMenu">
                {{ t('homeLayout.header.gooval') }}
              </a>
              <a class="header-menu-link" href="/#insurance-product" @click="closeMenu">
                {{ t('homeLayout.header.insurance') }}
              </a>
              <a class="header-menu-link" href="/#on-chain-data" @click="closeMenu">
                {{ t('homeLayout.header.blockchain') }}
              </a>
            </div>
          </Transition>
        </div>
      </div>
    </header>
    <main class="home-main">
      <router-view />
    </main>
    <footer class="home-footer">
      <div class="footer-content">
        <div class="footer-col footer-col1">
          <div class="footer-logo-row">
            <img class="footer-logo" src="@/assets/img/layout/logo.png" alt="GWOFY Logo" />
          </div>
          <div class="footer-socials">
            <a class="footer-social" href="https://x.com/GWOFY2025" target="_blank">
              <img src="@/assets/img/layout/social-twitter.png" alt="X" />
            </a>
            <a class="footer-social" href="https://www.linkedin.com/company/gwofy-crossborder" style="width:38px;" target="_blank">
              <img src="@/assets/img/layout/icons8-linkedin-240.svg" alt="LinkedIn" style="width:100%;" />
            </a>
          </div>
        </div>
        <div class="footer-col footer-col2"></div>
        <div class="footer-col footer-col2">
          <div class="footer-title">{{ t('homeLayout.footer.contactInfo') }}</div>
          <div class="footer-link">
            {{ t('homeLayout.footer.email') }}
            <a href="mailto:contact@gwofy.com">contact@gwofy.com</a>
          </div>
          <div class="footer-link">{{ t('homeLayout.footer.address') }}</div>
        </div>
        <div class="footer-col footer-col3">
          <div class="footer-title">{{ t('homeLayout.footer.aboutUs') }}</div>
          <a class="footer-link" href="/white-paper">{{ t('homeLayout.footer.whitePaper') }}</a>
          <a class="footer-link" href="/term-of-use">{{ t('homeLayout.footer.termsOfUse') }}</a>
          <a class="footer-link" href="/compliance-disclaimer">{{ t('homeLayout.footer.complianceDisclaimer') }}</a>
        </div>
      </div>
      <div class="footer-disclaimer">
        <p v-html="t('homeLayout.footer.disclaimer1')"></p>
        <p v-html="t('homeLayout.footer.disclaimer2')"></p>
        <p v-html="t('homeLayout.footer.disclaimer3')"></p>
        <p class="footer-license" v-html="t('homeLayout.footer.license')"></p>
        <p class="footer-warning">{{ t('homeLayout.footer.warning') }}</p>
      </div>
      <div class="footer-copyright">{{ t('homeLayout.footer.copyright') }}</div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'HomeLayout'
}
</script>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useTranslation } from '@/utils/i18n'

const { t } = useTranslation()
const menuOpen = ref(false)

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value
}

const closeMenu = () => {
  menuOpen.value = false
}

const handleDocumentClick = (event) => {
  if (!event.target.closest('.header-menu--mobile')) {
    closeMenu()
  }
}

const handleEscape = (event) => {
  if (event.key === 'Escape') {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
  document.addEventListener('keydown', handleEscape)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
  document.removeEventListener('keydown', handleEscape)
})
</script>

<style lang="scss" scoped>
.home-layout {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.home-header {
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  padding: 18px 24px;

  @media (max-width: 768px) {
    padding: 16px 12px 0;
  }
}

.header-inner {
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.logo {
  height: 30px;
  aspect-ratio: 467/87;

  @media (max-width: 768px) {
    height: 20px;
  }

  @media (max-width: 480px) {
    height: 16px;
  }
}

.logo-link {
  text-decoration: none;
  display: block;
}

.header-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.header-nav--desktop {
  display: flex;
  align-items: center;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(7, 37, 62, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 24px rgba(6, 28, 48, 0.18);
}

.header-nav-links {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-nav-divider {
  width: 1px;
  height: 14px;
  background: rgba(255, 255, 255, 0.28);
}

.header-product-link {
  color: #ffffff;
  font-size: 14px;
  text-decoration: none;
  font-family: 'Aileron-Regular', sans-serif;
  white-space: nowrap;
  transition: color 0.2s ease, opacity 0.2s ease;
}

.header-product-link:hover {
  color: #7cc7e6;
}

.header-menu--mobile {
  display: none;
  position: relative;
}

.header-menu-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 10px;
  background: rgba(7, 37, 62, 0.82);
  color: #ffffff;
  cursor: pointer;
  backdrop-filter: blur(8px);
}

.menu-toggle-icon {
  width: 16px;
  height: 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  span {
    display: block;
    height: 2px;
    width: 100%;
    background: #ffffff;
    border-radius: 1px;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  &.open span:nth-child(1) {
    transform: translateY(5px) rotate(45deg);
  }

  &.open span:nth-child(2) {
    opacity: 0;
  }

  &.open span:nth-child(3) {
    transform: translateY(-5px) rotate(-45deg);
  }
}

.header-menu-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 180px;
  padding: 10px;
  border-radius: 12px;
  background: rgba(7, 37, 62, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 12px 28px rgba(6, 28, 48, 0.28);
  overflow: hidden;
}

.header-menu-link {
  display: block;
  padding: 10px 12px;
  border-radius: 8px;
  color: #ffffff;
  text-decoration: none;
  font-size: 14px;
  font-family: 'Aileron-Regular', sans-serif;
  transition: background 0.2s ease, color 0.2s ease;
}

.header-menu-link:hover {
  background: rgba(26, 127, 176, 0.22);
  color: #7cc7e6;
}

.menu-collapse-enter-active,
.menu-collapse-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.menu-collapse-enter-from,
.menu-collapse-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 768px) {
  .header-nav--desktop {
    display: none;
  }

  .header-menu--mobile {
    display: block;
  }
}

.home-main {
  flex: 1;
  min-height: 100vh;
  max-width: 100vw;
  overflow: hidden;
  background: #ffffff;
}

.home-footer {
  width: 100%;
  background: #222;
  padding: 32px 0 0 0;
  color: #ffffff;
  font-size: 16px;

  .footer-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 32px;
  }

  .footer-col {
    flex: 1;
    min-width: 120px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .footer-logo-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 6px;
  }

  .footer-logo {
    height: 30px;
  }

  .footer-brand {
    font-size: 32px;
    font-family: 'Times New Roman', Times, serif;
    color: #2c3e50;
    font-weight: bold;
    letter-spacing: 1px;
  }

  .footer-subtitle {
    color: #2c3e50;
    font-size: 18px;
    margin-top: 2px;
    letter-spacing: 1px;
    font-weight: 500;
    
    // 桌面版英文状态下的特殊样式
    &.en-desktop {
      font-size: 12px;
      line-height: 1.4;
      max-width: 280px;
      word-wrap: break-word;
      
      @media (max-width: 800px) {
        font-size: 18px;
        max-width: none;
      }
    }
  }

  .footer-socials {
    display: flex;
    align-items:center;
    gap: 10px;
    .footer-social {
      img {
        width: 24px;
        aspect-ratio: 1;
      }
    }
  }

  .footer-subtitle-bold {
    color: #2c3e50;
    font-weight: bold;
    margin-right: 2px;
  }

  .footer-title {
    font-weight: bold;
    font-size: 16px;
    color: #ffffff;
    margin-bottom: 2px;
    letter-spacing: 1px;
  }

  .footer-link {
    color: #ffffff;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
    transition: color 0.2s;
    text-decoration: none;
    a {
      color: #fff !important;
      text-decoration: underline !important;
    }
  }

  .footer-link:hover {
    color: #ffd700;
  }

  .footer-disclaimer {
    padding: 24px 32px;
    text-align: left;
    max-width: 1100px;
    margin: 0 auto;
    
    p {
      color: #ffffff;
      font-size: 12px;
      line-height: 1.6;
      margin: 0 0 5px 0;
      letter-spacing: 0.5px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .footer-license,
    .footer-warning {
      margin-top: 10px;
      font-weight: 600;
      letter-spacing: 0.3px;
    }

    .footer-warning {
      margin-top: 4px;
      color: #ffe8a3;
    }
  }

  .footer-copyright {
    text-align: center;
    color: #ffffff;
    font-size: 15px;
    margin-top: 0;
    padding: 25px 0;
    background: #000000;
    letter-spacing: 1px;
  }

  @media (max-width: 800px) {
    .footer-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 18px;
      padding: 0 10px;
    }

    .footer-col,
    .footer-col2,
    .footer-col3 {
      align-items: flex-start;
      text-align: left;
    }

    .footer-logo-row {
      justify-content: flex-start;
    }
    .footer-disclaimer {
      padding: 24px 10px;
    }
  }
}

/* 首页字体设置：默认Aileron，英文用Libre Baskerville */
.home-page,
.main-content,
.flow-section {
  font-family: 'Aileron', Arial, sans-serif;
}

.home-page :lang(en),
.home-page .en-font,
.main-content :lang(en),
.main-content .en-font,
.flow-section :lang(en),
.flow-section .en-font {
  font-family: 'Libre Baskerville', Arial, serif;
  font-style: normal;
}
</style>